import { Chip, IconButton } from '@mui/material';
import { Input } from 'react-chat-elements';
import styled, { css } from 'styled-components';
import 'react-chat-elements/dist/main.css';
import colors from '../../../../../styles/colors';
import { PrimaryButton } from '../../../../../components/Button/ActionButtons';

const COMMENT_BOX_CLASS_NAME = 'rce-mbox-comment';

const FilterButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  flex: 0 0 auto;
  padding: 8px 25px;
`;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const MessageListContainer = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 24px;

  .rce-mbox {
    max-width: 400px;
  }

  /* Allow attachment messages to be wider and display full images */
  .rce-mbox:has(.rce-mbox-photo),
  .rce-mbox:has(.rce-mbox-video) {
    max-width: 550px;
  }

  .rce-mbox-photo,
  .rce-mbox-video {
    max-width: 100%;
  }

  .rce-mbox-photo--img {
    max-height: 500px;
  }

  .rce-mbox-photo--img img {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 500px;
    object-fit: contain;
  }

  .rce-mbox-video--video {
    max-height: 500px;
  }

  .rce-mbox-video--video video {
    max-width: 100%;
    max-height: 500px;
    object-fit: contain;
  }

  .${COMMENT_BOX_CLASS_NAME} {
    .rce-mbox {
      background-color: ${colors.shades.primary7};

      .rce-mbox-text {
        font-style: italic;
        color: ${colors.shades.beta1};
      }

      .rce-mbox-right-notch {
        fill: ${colors.shades.primary7};
      }
    }
  }
`;

const StyledInput = styled(Input)`
  border: 1px solid ${({ $isComment }) => ($isComment ? colors.shades.primary3 : colors.shades.gamma3)};
  flex: 1;
  min-width: 0;
`;

const StyledChip = styled(Chip)`
  margin: 0;
  flex-shrink: 0;
`;

/*
 * In comment mode the whole composer takes on the same pale yellow as the comment bubbles
 * in the transcript, so the destination of what you are typing is legible before you type
 * it rather than after you have sent it.
 */
const PaddedDiv = styled.div`
  flex: 0 0 auto;
  padding: 10px 25px 12px;
  background-color: ${colors.shades.secondary10};
  border-top: 1px solid ${colors.shades.secondary7};

  ${({ $isComment }) => $isComment && css`
    background-color: ${colors.shades.primary7};
    border-top: 1px solid ${colors.shades.primary3};
  `}
`;

const ComposerControlsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 8px;
`;

const AttachmentsSlot = styled.div`
  && > div {
    margin: 0;
  }
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SplitSend = styled.div`
  display: inline-flex;
  align-items: stretch;
  flex-shrink: 0;
`;

/*
 * Shared by both comment-writing actions — Add Comment and Save Comment.
 *
 * Neither can use the shared `warning` variant: that variant is filled with primary7, which is
 * exactly the tint the composer takes on in comment mode, so the button would dissolve into its
 * own background and leave the adjacent Cancel Edit as the prominent control. A solid gold keeps
 * it reading as a primary action while staying unmistakably distinct from the dark Send Message
 * button, and makes the two comment-writing actions visually identical.
 */
const AddCommentButton = styled(PrimaryButton)`
  && {
    border: 1px solid ${colors.shades.primary6};
    color: ${colors.shades.secondary8};
    background: ${colors.shades.primary2};

    &:hover {
      background-color: ${colors.shades.primary4};
    }
  }
`;

const PrimarySendButton = styled(PrimaryButton)`
  && {
    border-radius: 5px 0 0 5px;
    border-right: none;
  }
`;

const CaretSendButton = styled(PrimaryButton)`
  && {
    border-radius: 0 5px 5px 0;
    padding-left: 5px;
    padding-right: 5px;
  }
`;

const anchorMenuStyles = css`
  font-size: 11px;
  height: 15px;
  padding: 10px;
  gap: 5px;
`;

// eslint-disable-next-line max-len
const IMsgIcon = 'data:image/png;base64,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';
// eslint-disable-next-line max-len
const EmailIcon = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAAAsTAAALEwEAmpwYAAAD8klEQVR4nO2YXU/bZhiGc7L+hdav7ZMdAmfTTjoiNceT9lOqorFqSKwtWru2CChfjUgZH+tWRldgq6pp6gB1E2Ns0k422u2c2CYhUZPYBttxpnt6DBuJ7Wx9g0UyKY90yfmw7/u5ZIMQsVh72tOe9vwvRrpodEk9+qjUU3ouX9JNuUdHpFzSTcr2Oi4aXZEt3nEVZ+Te0l353dKfcq+O06FUkXv1Ceo+8fLSe/q6fNlAM5Au62snkpD7jLtyn4Gm8r4+3tDyUp/RJfUbFanfRHMxKnK/0ckvcMUck66YaAk+MEe4BcQB84U0YKIVEAfMbf47cN00pOv7aAk+3Nf5BW7uo5WIcQvc3kcoN3WwawrY1Z1ouaZ42fV6+QWGDhBg0IB46yXemlCgFWw4jhMJWsH2MimbOsK6+QVGDuCH3VAPiz7KIpGMRkIr2F4WZVI2dYR18wuMHcAPu7EDrWAhkVIg3s4inlSgvLROsLx1lJU5zCrYXkdYN7/A5AH8sFs7x8XTCsShLOKpxiSOMzKHGUd3kzrCuvkFkhb8sMF07QIzKsSRPcSn1X8WeOXl61zLBtOBXoJfIGXBDxtO1y5StJCYUyGO7SE+82oS/3UNG04Hegl+gWkLftiddPhC8yrEiT3EZ/9dInBuMXgudYR18wvMWvDDxtPhi5VsJO6rEJN73pHeN3KOQwLj6UAvwS0gfmLDjzCp1BW48EADS+XBpnJ4c17D8h861KLtsfy77n1G39E5dG49AWFSCfQS/AKf2vAjJJXw5Rc0sHs57/jOUgYslQVLZSAkVQ96TZ/Rd9XnaiES1BHWzS+wYMOPMKUEl1/UwGZy3pHem5aD2V+LeHslg9c/Vj3o9dxvRe+7sGucaoEpJdBL8AssOvAj3FOrlreQ+GIXbC6P7kUNSp1HIvyRq702XfXDTB1h3fwCjxz4EWbU4wWWdsHu59D9iG/5Goklysh7GemjDOoI6+YXWHHgR5in3x4WEiu7YA9y6F5qbPkaiRXKyntZJEEdYd38Al858CN8lsH5ZQ3s8zwufLkLTY/gjznd9rIok7KpI6ybX+BJGQEeW2DLOs4/zkSyvFMlQZmUTR1h3fwCX5cRyhMLwsMshAUtWh5mvex6vfwC35TRSsS4BZ46uvi0jNbAKfELfOu+EFddtAJs1eX/twpbd++I6y5ahCFugbPrTid7Vq6wZy6aS7lybs3u4Bbw7sJ37gT73kWTGY01Oh3PcUbYKK+xDRfNQNgor77xC15rWOBvCbbpjrNNt8I2KzgdXJf94I6eePnqOfuz0ylsuSNsq7LNtlyDbVUQLS5lbgs/usPnfkJjz3x72tOe9sROe/4CWmpzNFk1IjsAAAAASUVORK5CYII=';
// eslint-disable-next-line max-len
const InstagramIcon = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48'><defs><linearGradient id='g' x1='0' y1='1' x2='1' y2='0'><stop offset='0%' stop-color='%23feda75'/><stop offset='35%' stop-color='%23fa7e1e'/><stop offset='65%' stop-color='%23d62976'/><stop offset='85%' stop-color='%239624c4'/><stop offset='100%' stop-color='%234f5bd5'/></linearGradient></defs><rect x='2' y='2' width='44' height='44' rx='12' fill='url(%23g)'/><rect x='13' y='13' width='22' height='22' rx='7' fill='none' stroke='white' stroke-width='3'/><circle cx='24' cy='24' r='6' fill='none' stroke='white' stroke-width='3'/><circle cx='32' cy='16' r='2.5' fill='white'/></svg>";

const EditingPreview = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 4px;
  background-color: ${colors.base.secondary3};
  border-radius: 4px;
  font-size: 13px;
  color: ${colors.shades.beta1};

  span {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const EditCommentButton = styled(IconButton)`
  padding: 2px;
  color: ${colors.shades.beta2};

  &:hover {
    color: ${colors.base.alpha};
  }

  svg {
    font-size: 14px;
  }
`;

const CommentWrapper = styled.div`
  position: relative;

  ${EditCommentButton} {
    position: absolute;
    top: 10px;
    right: 25px;
    z-index: 1;
    background-color: ${colors.shades.secondary10};
    border-radius: 50%;
    border: 1px solid ${colors.shades.gamma3};
    padding: 3px;
    color: ${colors.shades.beta2};
  }
`;

const IGStatusContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 2px 12px 8px;
  font-size: 12px;
  color: ${({ $isError }) => ($isError ? colors.base.danger : colors.shades.gamma6)};

  > span {
    flex-shrink: 1;
  }
`;

const BadgeWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const BadgeDot = styled.span`
  position: absolute;
  top: -5px;
  right: -5px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${colors.base.danger};
  border: 2px solid ${colors.base.alpha};
  pointer-events: none;
`;

export {
  FilterButtonContainer,
  IMsgIcon,
  EmailIcon,
  InstagramIcon,
  StyledInput,
  StyledChip,
  PaddedDiv,
  ChatContainer,
  MessageListContainer,
  ComposerControlsRow,
  AttachmentsSlot,
  ToolbarActions,
  AddCommentButton,
  SplitSend,
  PrimarySendButton,
  CaretSendButton,
  anchorMenuStyles,
  COMMENT_BOX_CLASS_NAME,
  CommentWrapper,
  EditCommentButton,
  EditingPreview,
  IGStatusContainer,
  BadgeWrapper,
  BadgeDot,
};
